/**
 * SlashMenu — the `/` command palette shown above the cursor.
 *
 * Receives the tiptap `SuggestionProps` (items, command, clientRect, etc.)
 * and renders a cmdk list. Keyboard nav (↑/↓/Enter/Esc) is wired in
 * `onKeyDown` (called by the parent extension).
 *
 * Phase 3 ships block-type commands (Text, H1-H3, lists, quote, code,
 * divider). Phase 4 adds insert-as-template + insert-link actions.
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Command } from 'cmdk'
import { useSuggestionPosition } from '@/lib/tiptap/suggestionPopup'

export interface SlashItem {
  key: string
  title: string
  description?: string
  /**
   * Queues the command on the given TipTap chain. The slash extension runs
   * the chain once after `run` returns, so an item must NOT call `.run()`
   * itself — that would apply the command twice.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  run: (chain: any) => void
}

export interface SlashMenuRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export interface SlashMenuProps {
  items: SlashItem[]
  command: (item: SlashItem) => void
  query?: string
  clientRect?: (() => DOMRect | null) | null
}

export const SlashMenu = forwardRef<SlashMenuRef, SlashMenuProps>(function SlashMenu(
  { items, command, query, clientRect },
  ref,
) {
  const [hover, setHover] = useState(0)
  const pos = useSuggestionPosition(clientRect)

  useEffect(() => {
    setHover(0)
  }, [query, items.length])

  useImperativeHandle(ref, () => ({
    onKeyDown({ event }) {
      if (event.key === 'ArrowDown') {
        setHover((h) => (h + 1) % Math.max(items.length, 1))
        return true
      }
      if (event.key === 'ArrowUp') {
        setHover((h) => (h - 1 + items.length) % Math.max(items.length, 1))
        return true
      }
      if (event.key === 'Enter') {
        const item = items[hover]
        if (item) command(item)
        return true
      }
      return false
    },
  }))

  return (
    <div
      className="z-50 w-72 overflow-hidden rounded-md border border-surface-200 bg-white shadow-lg dark:border-surface-700 dark:bg-surface-800"
      style={pos ? { position: 'fixed', top: pos.top, left: pos.left } : { display: 'none' }}
    >
      <Command shouldFilter={false} label="Slash command palette">
        <Command.List>
          {items.length === 0 && <div className="px-3 py-2 text-xs text-surface-500">No matches.</div>}
          {items.map((it, i) => (
            <Command.Item
              key={it.key}
              value={it.title}
              onSelect={() => command(it)}
              onMouseMove={() => setHover(i)}
              className={
                'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs ' +
                (i === hover ? 'bg-surface-100 dark:bg-surface-700' : '')
              }
            >
              <span className="flex h-6 w-6 items-center justify-center rounded bg-surface-200 font-mono text-[10px] dark:bg-surface-700">
                {it.key}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{it.title}</span>
                {it.description && (
                  <span className="block truncate text-[10px] text-surface-500">{it.description}</span>
                )}
              </span>
            </Command.Item>
          ))}
        </Command.List>
      </Command>
    </div>
  )
})
